import { Divider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo, useState } from 'react';
import { useWorkflowCatalog } from '../../lib/workflows.hooks.js';
import { CatalogFilterBar } from './CatalogFilterBar.js';
import { applyFilters, deriveFilterOptions, EMPTY_FILTERS } from './catalogFilters.js';
import { WorkflowCard } from './WorkflowCard.js';
import type { CatalogGroup } from './workflows.types.js';

const GRID_COLUMNS = {
  xs: '1fr',
  md: 'repeat(2, minmax(0, 1fr))',
  lg: 'repeat(3, minmax(0, 1fr))',
};

interface WorkflowsCatalogProps {
  onOpenWorkflow: (workflowId: string, goal?: string) => void;
}

function StageSection({
  group,
  onOpen,
}: {
  group: CatalogGroup;
  onOpen: (workflowId: string, goal?: string) => void;
}): ReactNode {
  return (
    <Stack sx={{ gap: 3 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
        {group.label} ({group.items.length})
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 4 }}>
        {group.items.map((card) => (
          <WorkflowCard
            key={`${group.stage}:${card.workflowId}`}
            card={card}
            stage={group.stage}
            stageLabel={group.label}
            onOpen={onOpen}
          />
        ))}
      </Box>
    </Stack>
  );
}

export function WorkflowsCatalog({ onOpenWorkflow }: WorkflowsCatalogProps): ReactNode {
  const catalog = useWorkflowCatalog();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const groups = catalog.data?.groups ?? [];
  const options = useMemo(() => deriveFilterOptions(groups), [groups]);
  const visibleGroups = useMemo(() => applyFilters(groups, filters), [groups, filters]);

  if (catalog.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (catalog.isError) {
    return <Alert severity="error">Workflows could not be loaded.</Alert>;
  }

  const visibleCount = new Set(
    visibleGroups.flatMap((group) => group.items.map((card) => card.workflowId)),
  ).size;

  return (
    <Stack sx={{ gap: 6 }}>
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h2">Workflows</Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          Browse pre-set workflows and run to produce reports, briefs, storyboards and analytics.
        </Typography>
      </Stack>

      <Stack sx={{ gap: 4 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
          All workflows ({visibleCount})
        </Typography>
        <CatalogFilterBar options={options} filters={filters} onChange={setFilters} />
        <Divider />
      </Stack>

      {visibleCount === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>No workflows match these filters.</Typography>
      ) : (
        <Stack sx={{ gap: 8 }}>
          {visibleGroups.map((group) => (
            <StageSection key={group.stage} group={group} onOpen={onOpenWorkflow} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
